import React, { useMemo, useState } from "react";
import { Search, X, MapPin, Building2, AlertTriangle, RefreshCw, PartyPopper } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { COLORS, LEVEL_SINGULAR } from "@/lib/api";

const ALL = "__all__";

const SUGGEST_COLOR = { secciones: COLORS.guinda, areas: COLORS.areas, colonias: COLORS.colonias };
const SUGGEST_TAG = { seccion: "Sección", area: "Área", colonia: "Colonia" };

function FilterSelect({ label, value, onChange, options, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
        <SelectTrigger data-testid={testid} className="h-9 text-sm bg-white border-slate-200">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ALL}>Todas</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function FiltersPanel({
  filters, values, setValue, layers, toggleLayer, onReset,
  onSync, syncing, meta, resultCount,
  activeLayer, activeLevel, selectedValue, onSelectValue, hasSelection,
  searchText, setSearchText, onSearchSubmit, onPickSuggestion,
}) {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const term = (searchText || "").trim();
  const suggestions = useMemo(() => {
    if (!filters || term.length < 1) return [];
    const out = [];
    if (/^\d+$/.test(term)) {
      (filters.secciones || []).filter((s) => s.startsWith(term)).slice(0, 6)
        .forEach((s) => out.push({ type: "seccion", layer: "secciones", value: s, label: `Sección ${s}` }));
      (filters.areas || []).filter((a) => a.startsWith(term)).slice(0, 4)
        .forEach((a) => out.push({ type: "area", layer: "areas", value: a, label: `Área ${a}` }));
    } else {
      const up = term.toUpperCase();
      (filters.colonias || []).filter((c) => c.toUpperCase().includes(up)).slice(0, 8)
        .forEach((c) => out.push({ type: "colonia", layer: "colonias", value: c, label: c }));
    }
    return out.slice(0, 8);
  }, [filters, term]);

  if (!filters) return null;

  const categoriaOptions =
    layers.actividades && !layers.rac
      ? filters.categorias_actividades
      : !layers.actividades && layers.rac
      ? filters.categorias_rac
      : [...new Set([...filters.categorias_actividades, ...filters.categorias_rac])].sort();

  const territorialOptions =
    activeLayer === "secciones" ? (filters.secciones || []) :
    activeLayer === "areas" ? (filters.areas || []) :
    activeLayer === "colonias" ? (filters.colonias || []) : [];
  const territorialLabel = LEVEL_SINGULAR[activeLevel] || "Unidad territorial";

  return (
    <div className="space-y-4">
      {/* Search + autocomplete */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-600">Buscar colonia, sección, área, dirección o folio</label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-input"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setSuggestOpen(true); }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); setSuggestOpen(false); onSearchSubmit(); }
                else if (e.key === "Escape") { setSuggestOpen(false); }
              }}
              placeholder="Ej. Flamboyanes, 1304, 5..."
              className="pl-9 h-9 text-sm bg-white border-slate-200"
              autoComplete="off"
            />
            {suggestOpen && suggestions.length > 0 && (
              <div data-testid="search-suggestions"
                className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden max-h-64 overflow-y-auto">
                {suggestions.map((s, idx) => (
                  <button key={`${s.type}-${s.value}`}
                    data-testid={`suggestion-${s.type}-${idx}`}
                    onMouseDown={(e) => { e.preventDefault(); setSuggestOpen(false); onPickSuggestion(s); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: SUGGEST_COLOR[s.layer] }} />
                    <span className="flex-1 truncate text-slate-700">{s.label}</span>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">{SUGGEST_TAG[s.type]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button data-testid="search-btn" onClick={() => { setSuggestOpen(false); onSearchSubmit(); }}
            className="h-9 px-3 bg-guinda hover:bg-guinda-dark text-white shrink-0">
            <Search className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Point layer toggles */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-slate-600">Tipo de registro</label>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 bg-white">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: COLORS.actividades }} />
            <Building2 className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-700">Actividades</span>
          </div>
          <Switch data-testid="toggle-actividades" checked={layers.actividades}
            onCheckedChange={() => toggleLayer("actividades")} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 bg-white">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: COLORS.rac }} />
            <AlertTriangle className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-700">Reportes (RAC)</span>
          </div>
          <Switch data-testid="toggle-rac" checked={layers.rac}
            onCheckedChange={() => toggleLayer("rac")} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 bg-white">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: COLORS.activaciones }} />
            <PartyPopper className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-700">Activaciones</span>
          </div>
          <Switch data-testid="toggle-activaciones" checked={layers.activaciones}
            onCheckedChange={() => toggleLayer("activaciones")} />
        </div>
      </div>

      <div className="h-px bg-slate-200" />

      {/* Territorial selector driven by active layer */}
      <FilterSelect label={territorialLabel} value={selectedValue}
        onChange={onSelectValue} options={territorialOptions}
        testid="filter-territorial" placeholder={`Todas las ${territorialLabel.toLowerCase()}s`} />

      <FilterSelect label="Unidad Responsable" value={values.unidad}
        onChange={(v) => setValue("unidad", v)} options={filters.unidades}
        testid="filter-unidad" placeholder="Todas" />
      <FilterSelect label="Categoría / Asunto" value={values.categoria}
        onChange={(v) => setValue("categoria", v)} options={categoriaOptions}
        testid="filter-categoria" placeholder="Todas" />

      <Button data-testid="reset-filters" variant="outline" onClick={onReset}
        className="w-full h-9 text-sm border-slate-200 text-slate-600 hover:bg-slate-50">
        <X className="w-4 h-4 mr-1.5" /> Limpiar filtros
      </Button>

      <div className="h-px bg-slate-200" />

      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 space-y-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <MapPin className="w-3.5 h-3.5 text-guinda" />
          <span data-testid="result-count" className="font-semibold text-slate-800">
            {(resultCount || 0).toLocaleString("es-MX")}
          </span>
          puntos en el mapa
        </div>
        {meta?.last_sync && (
          <div className="text-[10px] text-slate-400">
            Última sincronización: {new Date(meta.last_sync).toLocaleString("es-MX")}
          </div>
        )}
      </div>

      <Button data-testid="sync-btn" onClick={onSync} disabled={syncing}
        className="w-full h-9 text-sm bg-guinda hover:bg-guinda-dark text-white">
        <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
        {syncing ? "Sincronizando..." : "Actualizar datos"}
      </Button>
    </div>
  );
}
